import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { GoogleLogo, CheckCircle, XCircle } from '@phosphor-icons/react';
import TanowMark from '@/components/TanowMark';
import Footer from '@/components/Footer';

export default function AcceptInvite() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, error: null, invite: null });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/invites/${token}`);
                setState({ loading: false, invite: data, error: null });
            } catch (err) {
                setState({ loading: false, invite: null, error: err.response?.data?.detail || 'Invite not found.' });
            }
        })();
    }, [token]);

    const startSignIn = () => {
        const redirectUrl = `${window.location.origin}/dashboard`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <div className="flex-1 flex items-center justify-center px-6 py-16">
                <div className="w-full max-w-md text-center">
                    <div className="mx-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center mb-6">
                        <TanowMark size={30} />
                    </div>
                    {state.loading ? (
                        <div className="text-slate-500 text-sm">Checking your invite…</div>
                    ) : state.error ? (
                        <>
                            <div className="mx-auto w-10 h-10 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-3"><XCircle size={20} /></div>
                            <h1 className="font-heading text-3xl text-slate-900">Invite not found</h1>
                            <p className="text-slate-600 mt-2">{state.error}</p>
                        </>
                    ) : state.invite?.status === 'redeemed' ? (
                        <>
                            <div className="mx-auto w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3"><CheckCircle size={20} /></div>
                            <h1 className="font-heading text-3xl text-slate-900">Already redeemed</h1>
                            <p className="text-slate-600 mt-2">This invite has been used. Sign in with the Google account you used to accept it.</p>
                            <Button data-testid="accept-invite-signin" onClick={startSignIn} className="mt-6 w-full h-12 rounded-full bg-primary text-primary-foreground">
                                <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
                            </Button>
                        </>
                    ) : state.invite?.status === 'expired' ? (
                        <>
                            <div className="mx-auto w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mb-3"><XCircle size={20} /></div>
                            <h1 className="font-heading text-3xl text-slate-900">Invite expired</h1>
                            <p className="text-slate-600 mt-2">This invite has expired. Ask your admin to send a fresh one.</p>
                        </>
                    ) : (
                        <>
                            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">You&apos;re invited</div>
                            <h1 className="font-heading text-4xl text-slate-900">Welcome to Tanow{state.invite?.name ? `, ${state.invite.name.split(' ')[0]}` : ''}</h1>
                            <p className="text-slate-600 mt-3 leading-relaxed">
                                {state.invite?.invited_by_name || 'Your admin'} invited you as a coordinator on Tanow.
                                Sign in with the Google account for <b>{state.invite?.email}</b> to accept.
                            </p>
                            <Button data-testid="accept-invite-signin" onClick={startSignIn} className="mt-6 w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                                <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
                            </Button>
                            <p className="text-[11px] text-slate-500 mt-4">
                                Use the same email address ({state.invite?.email}) — this is how we recognize you.
                            </p>
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}
